import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { stytchService } from '../utils/stytchService';
import { clearSessionData } from "../store/sessionSlice";
import { unhideEncryptedToken } from "./tokenEncryptionUtils";
import {
  selectUserAuthSource,
  selectSubClientMode,
  AuthenticationSource,
} from "../store/authPreferencesSlice";
import { clearExperienceData } from "../store/experienceSlice";
import { clearUser } from "../store/userSlice";
import { popUser } from "../store/userSlice";

export type GetNavOptions = {
  includeParams?: boolean;
  extraParams?: string;
};

export type GoNavOptions = GetNavOptions & {
  replace?: boolean;
  cameViaIdP?: boolean;
};

export function logInTarget(authSource?: AuthenticationSource) {
  switch (authSource) {
    case AuthenticationSource.DevEmail:
      return "/devlogin";
    case AuthenticationSource.Token:
      return "/tokenlogin";
    case AuthenticationSource.AssessmentGoogle:
    case AuthenticationSource.AssessmentLinkedIn:
    case AuthenticationSource.AssessmentGithub:
    case AuthenticationSource.AssessmentEmail:
      return "/login";
    default:
      return "/" // ?
  }
}

export function sessionCreationTarget(authSource?: AuthenticationSource) {
  switch (authSource) {
    case AuthenticationSource.DevEmail:
      return "/manual-connection";
    case AuthenticationSource.Token:
      return "/avatarstreamer"; // TODO is this right?
    case AuthenticationSource.AssessmentGoogle:
    case AuthenticationSource.AssessmentLinkedIn:
    case AuthenticationSource.AssessmentGithub:
    case AuthenticationSource.AssessmentEmail:
      return "/universal-session-lobby";
    default:
      return "/" // ?
  }
}

export function sessionEndTarget(authSource?: AuthenticationSource) {
  switch (authSource) {
    case AuthenticationSource.DevEmail:
      return "/manual-connection";
    case AuthenticationSource.Token:
      return "/investor-session-end";
    case AuthenticationSource.AssessmentGoogle:
    case AuthenticationSource.AssessmentLinkedIn:
    case AuthenticationSource.AssessmentGithub:
    case AuthenticationSource.AssessmentEmail:
      return "/universal-session-end";
    default:
      return "/" // ?
  }
}

export function oauthFilteredParams(params: URLSearchParams) {
  const filtered = new URLSearchParams(params);
  if (params.has("stytch_token_type")) {
    filtered.delete("stytch_token_type");
    filtered.delete("token");
  }
  return filtered;
}

export function removeAuthQueryParamsFromUrl() {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  if (params.has("stytch_token_type")) {
    params.delete("token");
    params.delete("stytch_token_type");

    const newUrl = `${url.pathname}${params.toString() ? "?" + params.toString() : ""}${url.hash}`;
    window.history.replaceState({}, "", newUrl);
  }
}

/**
 * Navigation helpers for the app.
 */
export function useNavUtils() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const authSource = useSelector(selectUserAuthSource);
  const subClientMode = useSelector(selectSubClientMode);

  const getLoginTarget = useCallback(() => {
    return logInTarget(authSource);
  }, [authSource]);

  const getSessionCreationTarget = useCallback(() => {
    return sessionCreationTarget(authSource);
  }, [authSource]);

  // Creates a navigation destination that includes existing query parameters and adds others
  const getNav = useCallback(
    (path: string, { includeParams = true, extraParams }: GetNavOptions) => {
      let query = "";

      if (includeParams || extraParams !== undefined) {
        const parts: string[] = [];

        if (includeParams && search) {
          const currentParams = new URLSearchParams(search);
          const filtered = oauthFilteredParams(currentParams);
          const filteredString = filtered.toString();
          if (filteredString) parts.push(filteredString);
        }

        if (extraParams !== undefined) {
          parts.push(
            extraParams.startsWith("?") ? extraParams.slice(1) : extraParams
          );
        }

        query = parts.length ? `?${parts.join("&")}` : "";
      }

      return `${path}${query}`;
    },
    [search]
  );

  const restoreSubClientParent = () => {
    const tokenRestored = unhideEncryptedToken();
    if (tokenRestored) {
      console.log("[navUtils] Logout restored encrypted token from hidden state");
      dispatch(clearSessionData());
      dispatch(popUser());
      console.log("[navUtils] Logout popped user");
      return true;
    }
    return false;
  }

  // Navigates to a path, with extra powers.
  const goNav = useCallback((path: string, options: GoNavOptions = {}) => {
    const {
      includeParams = true,
      extraParams = undefined,
      replace = true,
      cameViaIdP = undefined,
    } = options;
    const dest = getNav(path, { includeParams, extraParams });
    console.log("[navUtils] goNav to", dest);
    navigate(dest, {
      replace,
      ...(cameViaIdP !== undefined ? { state: { cameViaIdP } } : {}),
    });
  }, [navigate, getNav]);

  // Logout helper
  const logoutReset = useCallback(async (totalReset: boolean = false, errorMessage?: string) => {
    if (subClientMode && !totalReset) {
      if (restoreSubClientParent()) {
        return;
      }
    }

    if (totalReset) {
      console.log("[navUtils] Logout performing total reset");
    } else {
      console.log("[navUtils] Logout performing partial reset");
    }

    try {
      await stytchService.logout();
      console.log("[navUtils] Logout Successfully logged user out from stytch");
    } catch (error) {
      console.error("[navUtils] Logout Failed to revoke stytch session:", error);
    }

    // If we have an error message, store it for the next page
    if (errorMessage) {
      try {
        sessionStorage.setItem(
          "postLogoutAlert",
          JSON.stringify({ type: "error", errorMessage })
        );
      } catch { }
    }

    if (totalReset) {
      console.log("[navUtils] Logout clearing all localStorage and resetting Redux state");
      localStorage.clear();
      sessionStorage.clear();

      dispatch({ type: "RESET_APP_STATE" });

      // Let Redux state update propagate and persistence complete, clear again.
      // This is bc of redux-backed-by-localstorage can generate races.
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        console.log("[navUtils] localStorage cleared after Redux reset");
      }, 10);
    } else {
      // Clear authentication tokens and user data, but preserve auth preferences
      localStorage.removeItem("stytch_session");
      localStorage.removeItem("anon_user_id");
      localStorage.removeItem("join_token");

      dispatch(clearSessionData());
      dispatch(clearUser());
      // yeah no: dispatch(clearExperienceData());

      // The previous way. Not very certain of the above. Leave this here.
      // // Reset Redux state but preserve auth preferences
      // // This will clear session data but keep auth preferences intact,
      // // and thus will trigger RequireAuth, which will
      // // direct to the appropriate login page.
      // dispatch({ type: "RESET_APP_STATE_PRESERVE_AUTH_PREFS" });

      // // Clear user_, session_ and experience_  prefixed localStorage items
      // // (but not authPrefs_ since we preserve those)
      // setTimeout(() => {
      //   Object.keys(localStorage).forEach(key => {
      //     if (key.startsWith('user_') || key.startsWith('session_') || key.startsWith('experience_')) {
      //       localStorage.removeItem(key);
      //     }
      //   });
      // }, 10);
    }

    console.log("[navUtils] Logout finished");

  }, [dispatch, subClientMode]);

  // Session end helper
  const sessionEndNav = useCallback((clearSession: boolean = true) => {
    const target = sessionEndTarget(authSource);
    if (!subClientMode) {
      if (clearSession) {
        setTimeout(() => {
          dispatch(clearSessionData());
        }, 100); // Small delay to let navigation finish
      }
      goNav(target);
    } else if (subClientMode) {
      restoreSubClientParent();
    }
  }, [authSource, goNav, dispatch, subClientMode]);

  return useMemo(
    () => ({
      getLoginTarget,
      getSessionCreationTarget,
      getNav,
      goNav,
      logoutReset,
      sessionEndNav,
    }),
    [getLoginTarget, getSessionCreationTarget, getNav, goNav, logoutReset, sessionEndNav]
  );
}
